import * as React from 'react';

import { Features, core } from '@tock/coretypes';
import PureBaseComponent from '../components/PureBaseComponent';

export const FeatureFlagContext = React.createContext<Features>({});

export type FeatureFlagPredicate = (feature: core.Feature.FeatureName) => boolean;
const isFeatureEnabled = (feature: core.Feature.FeatureName, features: Features): boolean => {
  return features[feature] || false;
};

export interface IWithFeatureFlagProps {
  features: Features;
  isFeatureEnabled: FeatureFlagPredicate;
  shouldShowHiddenFlexFeatures(): boolean;
}

interface WithFeatureFlagHelperProps {
  features: Features;
  children(props: IWithFeatureFlagProps): React.ReactNode;
}

class WithFeatureFlagHelper extends React.Component<WithFeatureFlagHelperProps> {
  isFeatureEnabled = (feature: core.Feature.FeatureName): boolean => {
    return this.props.features[feature] || false;
  };

  shouldShowHiddenFlexFeatures = (): boolean => {
    return this.isFeatureEnabled('DISPLAY_FEATURES_HIDDEN_IN_FLEX');
  };

  render() {
    const { features } = this.props;
    return this.props.children({
      features,
      isFeatureEnabled: this.isFeatureEnabled,
      shouldShowHiddenFlexFeatures: this.shouldShowHiddenFlexFeatures,
    });
  }
}

export function withFeatureFlags<P extends {}>(
  WrappedComponent: React.ComponentType<React.PropsWithChildren<P & IWithFeatureFlagProps>>
): React.ComponentClass<P> {
  class WithFeatureFlags extends PureBaseComponent<P> {
    static displayName: string;

    renderFeatureFlagHelper(features: Features) {
      return (
        <WithFeatureFlagHelper features={features}>{this.renderChildren}</WithFeatureFlagHelper>
      );
    }

    renderChildren(featureProps: IWithFeatureFlagProps) {
      return <WrappedComponent {...this.props} {...featureProps} />;
    }

    render() {
      return (
        <FeatureFlagContext.Consumer>{this.renderFeatureFlagHelper}</FeatureFlagContext.Consumer>
      );
    }
  }
  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithFeatureFlags.displayName = `WithFeatureFlags(${wrappedComponentName})`;
  return WithFeatureFlags;
}

/**
 * Accepts the name of a flag and returns a boolean indicating whether that flag
 * is enabled or disabled.
 */
export function useFeatureFlag(flag?: core.Feature.FeatureName): boolean {
  const flags = React.useContext(FeatureFlagContext);
  if (!flag) {
    return false;
  }
  return !!flags[flag];
}

/**
 * If using multiple feature flags in a single function component, one can use this hook to obtain a predicate for checking them
 */
export function useFeatureFlags(): FeatureFlagPredicate {
  const flags = React.useContext(FeatureFlagContext);
  return (feature: core.Feature.FeatureName) => {
    return isFeatureEnabled(feature, flags);
  };
}

type FeatureFlagRenderPropFunction = (isFeatureFlagEnabled: boolean) => React.ReactNode;

interface FFProps1 {
  ifEnabled: core.Feature.FeatureName;
  doNotShow?: boolean;
  children: React.ReactNode;
}

interface FFProps2 {
  ifEnabled: core.Feature.FeatureName;
  children: FeatureFlagRenderPropFunction;
}
type FeatureFlagProps = FFProps1 | FFProps2;
export default class FeatureFlag extends PureBaseComponent<FeatureFlagProps> {
  static defaultProps = {
    doNotShow: false,
  };
  renderChildren(features: Features) {
    const { ifEnabled } = this.props as FFProps1;
    const isFeatureFlagEnabled = features[ifEnabled] || false;
    if (typeof this.props.children === 'function') {
      return (this.props.children as FeatureFlagRenderPropFunction)(isFeatureFlagEnabled);
    } else {
      const { doNotShow } = this.props as FFProps1;
      if (isFeatureFlagEnabled && !doNotShow) {
        return this.props.children;
      } else if (!isFeatureFlagEnabled && doNotShow) {
        return this.props.children;
      }
    }
    return null;
  }

  render() {
    return <FeatureFlagContext.Consumer>{this.renderChildren}</FeatureFlagContext.Consumer>;
  }
}
